import { utils } from '@ps/poker-games-tile-mappers';
import { canUseDOM } from '@ps/web-core-utils';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import { useMTTGameUrl, useOpenActiveGame } from '@swf/poker';

import useTournamentStatus from '../../hooks/useTournamentStatus';
import { usePokerPowerPathWidget } from '../../model';
import type { StepKey } from '../../types';
import { StepKeyName } from '../../types';
import { getPrizePoolDisplayValue, getPrizePoolLabel } from '../../utils/prizePool';
import Tile from '../Tile/Tile';

import {
  TXTCLI_ON_DEMAND,
  TXTCLI_Play_Now,
  TXTCLI_Register__toTournament,
  TXTCLI_Step_N0_ticket
} from './StepScheduled.messages.json';
import { TILE_BANNER_CLASSES_MAP } from './constants';

interface Props {
  stepKey: StepKey;
}

const StepScheduled: React.FC<Props> = ({ stepKey }) => {
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const locale = useSelector(select.locale.getIntlLocale);
  const timeZoneName = useSelector(select.timeZone.getTimeZoneName);
  const { modal } = useDispatch<Dispatch>();
  const { formatMessage } = useIntl();
  const { widgetSelect } = usePokerPowerPathWidget();
  const selectedStep = useSelector(
    (rootState: RootState) => widgetSelect.getStepByKey(rootState, stepKey),
    shallowEqual
  );
  const isLoading = useSelector(widgetSelect.isLoading);
  const { getStatus } = useTournamentStatus();
  const tableUrl = useMTTGameUrl(selectedStep?.id);
  const openActiveGame = useOpenActiveGame({ tableUrl });

  const handleOnRegister = useCallback(() => {
    if (isAuthenticated) {
      openActiveGame();
    } else {
      modal.enqueue({ path: '/auth/login' });
    }
  }, [isAuthenticated, modal, openActiveGame]);

  if (!selectedStep) {
    return null;
  }

  const {
    key,
    formattedBuyIn,
    step,
    currency,
    whenStartInt,
    regEndInt,
    legacyStatus,
    restricted,
    enrolled,
    tournFlagsServInt
  } = selectedStep;

  const enrolledCount = key === StepKeyName.step2SNG ? '16-max' : enrolled || '-';
  const tournamentState = utils.mttUtils.getMttState(legacyStatus, restricted, tournFlagsServInt);
  let formattedStatus = formatMessage(TXTCLI_ON_DEMAND);
  let playButtonTranslation = TXTCLI_Play_Now;
  let formattedValueStyles = '';
  const buyIn =
    key === StepKeyName.step4 ? formatMessage(TXTCLI_Step_N0_ticket, { 0: 4 }) : formattedBuyIn;
  let statusLoading;

  if (whenStartInt && whenStartInt > 0 && regEndInt && regEndInt > 0) {
    const { formattedValue, valueStyle } = getStatus(tournamentState, regEndInt, whenStartInt);
    formattedStatus = formattedValue;
    statusLoading = isLoading || !canUseDOM;
    playButtonTranslation = TXTCLI_Register__toTournament;
    formattedValueStyles = valueStyle;
  }

  return (
    <Tile
      prize={getPrizePoolDisplayValue(key, selectedStep, locale, false, timeZoneName)}
      prizeLabel={getPrizePoolLabel(key)}
      buyIn={buyIn}
      stepNumber={step}
      statusLoading={statusLoading}
      currency={currency}
      enrolled={enrolledCount}
      status={formattedStatus}
      stateClassName={formattedValueStyles}
      stepKey={key}
      onRegister={handleOnRegister}
      bannerClassName={TILE_BANNER_CLASSES_MAP[key]}
      playButtonTranslation={playButtonTranslation}
    />
  );
};
export default StepScheduled;
